import React, { useState } from "react";
import { Formik } from "formik";
import * as Yup from "yup";
import {
    withStyles,
    Button,
    Divider,
    CircularProgress,
    Typography,
    InputLabel,
    Grid, Switch, Tabs, Tab, FormGroup, FormControlLabel, Checkbox, MenuItem, Select, FormControl
} from "@material-ui/core";
import { verify as verificationMethods, valuesDiskHealth } from '../sc-utilities/CustomerUtilities';
import WifiForm from "../sc-settings/Presets/WifiForm";
import BiosPasswordForm from "../sc-settings/Presets/Work/BiosPasswordForm";
import { Delete, ArrowUpward, ArrowDownward } from "@material-ui/icons";
import { snackActions } from "../sc-utilities/SnackBar";
import { updateURL } from "../sc-utilities";
import PresetMobileSettings from "./PresetMobileSettings";
import PresetWorkSettings from "./PresetWorkSettings";
import { getTabValue, TabContainer, TabPanel } from "../sc-order/Order/Details";
import { TimePicker } from "@material-ui/pickers";
import moment from 'moment';

const styles = theme => ({
    root: {
        height: 'auto',
        width: '100%',
        marginBottom: theme.spacing(2),
    },
    form: {
        width: '95%',
        margin: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    submit: {
        width: "100%",
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    addButton: {
        marginTop: theme.spacing(3),
        marginBottom: theme.spacing(2),
        borderRadius: '1px',
        '&:hover': {
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.buttonText.default
        }
    },
    text: {
        borderRadius: '1px',
        marginRight: 10,
        color: theme.palette.buttonText.default
    },
    divider: {
        margin: theme.spacing(1),

    },
    wifiDivider: {
        margin: "15px 0px",
    },
    formControl: {
        width: "100%"
    },
    row: {
        display: 'row',
        width: "auto",
        alignItems: 'center'
    },
    osType: {
        marginTop: theme.spacing(2),
    },
    geolocation: {
        padding: "10px 0",
        marginTop: "10px"
    },
    androidIos: {
        marginTop: "20px"
    },
});

export function valueText(value) {
    return `${value}%`;
}

export const enabledField = (values) => {
    const tabValue = getTabValue();
    if (tabValue === 1) {
        return !(values?.["LooseDrives"]?.PresetsEnabled ?? values.PresetsEnabled);
    }
    return !(values?.["Normal"]?.PresetsEnabled ?? values.PresetsEnabled);
}

function PresetForm(props) {
    const { classes, theme, formSettings, initialValues, setLoading, loading, handleSubmit, translations, customerPresetFilter, updatePresets, loadPresets, securazeInitialValues, idParams } = props;
    const { printers, grades, operatingSystems, orders, collies, securazeSolutions, methods, skuPatterns } = customerPresetFilter;

    const [tabValue, setTabValue] = useState(getTabValue());

    const handleTabChange = (_event, value) => {
        updateURL(value);
        setTabValue(value);
    };

    return (
        <div className={classes.root}>
            <Formik
                initialValues={initialValues}
                onSubmit={async (values, { setSubmitting }) => {
                    setSubmitting(true);
                    await handleSubmit(values);
                    setSubmitting(false);
                }}
                enableReinitialize
                validationSchema={Yup.object().shape(formSettings.Validation)}
            >
                {formikProps => {
                    const { values, isSubmitting, setFieldValue, handleChange, isValid, setValues, handleSubmit, resetForm } = formikProps;

                    const workSKUPatterns = skuPatterns.filter(SKUPattern => SKUPattern.ProductType === 'PCProduct');
                    const mobileSKUPatterns = skuPatterns.filter(SKUPattern => SKUPattern.ProductType === 'MobileProduct')
                    const storageSKUPatterns = skuPatterns.filter(SKUPattern => SKUPattern.ProductType === 'StorageProduct')
                    const watchSKUPatterns = skuPatterns.filter(SKUPattern => SKUPattern.ProductType === 'WatchProduct')
                    const headphoneSKUPatterns = skuPatterns.filter(SKUPattern => SKUPattern.ProductType === 'HeadphoneProduct')

                    const getCurrentTabValues = getTabValue() === 1 ? values["LooseDrives"] : values["Normal"];

                    const gradeID = (grades ? grades : []).filter(grade => grade.value === getCurrentTabValues.GradeID).length > 0;
                    const operatingSystemID = (operatingSystems ? operatingSystems : []).filter(os => os.value === getCurrentTabValues.OperatingSystemID).length > 0;
                    const printerID = (printers ? printers : []).filter(printer => printer.value === getCurrentTabValues.PrinterID).length > 0;
                    const orderID = (orders ? orders : []).filter(order => order?.OrderID === getCurrentTabValues.OrderID).length > 0;
                    const transportColliID = (orders ? orders : []).filter(el => el.OrderID === getCurrentTabValues.OrderID ? el.OrderID : []).filter(colli => colli?.TransportCollies.filter(el => el.TransportColliID === values.TransportColliID)).length > 0;
                    const colliID = (collies ? collies : []).filter(colli => colli?.value === getCurrentTabValues.ColliID).length > 0;
                    const workSKUPatternID = (workSKUPatterns ? workSKUPatterns : []).filter(skuPattern => skuPattern?.SKUPatternID === getCurrentTabValues.SKUPatternID).length > 0;
                    const storageSKUPatternID = (storageSKUPatterns ? storageSKUPatterns : []).filter(skuPattern => skuPattern?.SKUPatternID === getCurrentTabValues.SKUPatternID).length > 0;
                    const watchSKUPatternID = (watchSKUPatterns ? watchSKUPatterns : []).filter(skuPattern => skuPattern?.SKUPatternID === getCurrentTabValues.SKUPatternID).length > 0;
                    const headphoneSKUPatternID = (headphoneSKUPatterns ? headphoneSKUPatterns : []).filter(skuPattern => skuPattern?.SKUPatternID === getCurrentTabValues.SKUPatternID).length > 0;

                    const addWifi = () => {
                        setValues({ ...values, Wifis: [...values.Wifis, { Security: "", SSID: "", Username: "", Password: "" }] })
                    }

                    const removeWifi = (index) => {
                        setValues({ ...values, Wifis: values.Wifis.filter((value, i) => i !== index) });
                    }

                    const addBiosPassword = () => {
                        setValues({ ...values, Normal: { ...values.Normal, BiosPasswords: { Enabled: values.Normal.BiosPasswords.Enabled, Passwords: [...values.Normal.BiosPasswords.Passwords, { Description: "", Password: "" }] } } })
                    }

                    const removeBiosPassword = (index) => {
                        setValues({ ...values, Normal: { ...values.Normal, BiosPasswords: { Enabled: values.Normal.BiosPasswords.Enabled, Passwords: values.Normal.BiosPasswords.Passwords.filter((value, i) => i !== index) } } });
                    }

                    const moveItemInArray = (array, from, to) => {
                        if (to < 0) {
                            to = 0;
                        } else if (to >= array.length) {
                            to = array.length - 1;
                        }
                        const item = array.splice(from, 1)[0];
                        array.splice(to, 0, item);
                    }

                    const moveBiosPasswordUp = (index) => {
                        const passwords = values.Normal.BiosPasswords.Passwords;
                        moveItemInArray(passwords, index, index - 1);
                        setValues({ ...values, Normal: { ...values.Normal, BiosPasswords: { Enabled: values.Normal.BiosPasswords.Enabled, Passwords: passwords } } });
                    }

                    const moveBiosPasswordDown = (index) => {
                        const passwords = values.Normal.BiosPasswords.Passwords;
                        moveItemInArray(passwords, index, index + 1);
                        setValues({ ...values, Normal: { ...values.Normal, BiosPasswords: { Enabled: values.Normal.BiosPasswords.Enabled, Passwords: passwords } } });
                    }

                    const handleChangeDifferentMobileSolutions = (e) => {
                        setValues({ ...values, ErasureMobile: { ...values["ErasureMobile"], iOS: values["ErasureMobile"]["Android"] }, DifferentMobileSolutions: e.target.checked })
                    }

                    const workProps = {
                        translations: translations,
                        handleChange: handleChange,
                        values: values,
                        orderID: orderID,
                        orders: orders,
                        transportColliID: transportColliID,
                        colliID: colliID,
                        collies: collies,
                        setFieldValue: setFieldValue,
                        verify: verificationMethods,
                        methods: methods,
                        gradeID: gradeID,
                        operatingSystemID: operatingSystemID,
                        printerID: printerID,
                        printers: printers,
                        grades: grades,
                        operatingSystems: operatingSystems,
                        setValues: setValues,
                        valuesDiskHealth: valuesDiskHealth,
                    }

                    return (
                        <form className={classes.form} onSubmit={e => {
                            e.preventDefault();
                            handleSubmit(e);
                        }}>
                            <Button className={classes.text} variant="contained" color={'primary'} onClick={async () => {
                                let serverResult;
                                resetForm();
                                serverResult = await updatePresets({ ...securazeInitialValues, ...idParams });
                                if (serverResult) {
                                    loadPresets();
                                    return setLoading(false);
                                }
                                if (serverResult?.status) {
                                    return snackActions.snackBarComponent("Presets updated.", "success");
                                } else {
                                    return snackActions.snackBarComponent("Error: Failed to update presets", "error");
                                }
                            }}>
                                {translations['reset'] || 'Reset'}
                            </Button>

                            {/*Work*/}
                            <Tabs
                                value={tabValue}
                                onChange={handleTabChange}
                                indicatorColor="primary"
                                textColor="primary"
                                variant="fullWidth"
                            >
                                <Tab label={translations['normal'] || "Normal"} value={0} />
                                <Tab label={translations['loose_drives'] || "Loose drives"} value={1} />
                            </Tabs>

                            <TabPanel index={0} value={tabValue} style={{ padding: 3 * 3 }}>
                                <TabContainer dir={theme.direction}>
                                    <PresetWorkSettings {...{...workProps, skuPatterns: workSKUPatterns, skuPatternID: workSKUPatternID}} eraseOnlyName={"Normal"} />
                                </TabContainer>
                            </TabPanel>
                            <TabPanel index={1} value={tabValue} style={{ padding: 3 * 3 }}>
                                <TabContainer dir={theme.direction}>
                                    <PresetWorkSettings {...{...workProps, skuPatterns: storageSKUPatterns, skuPatternID: storageSKUPatternID}} eraseOnlyName={"LooseDrives"} />
                                </TabContainer>
                            </TabPanel>

                            <Divider className={classes.divider} variant="middle" />
                            <br />

                            <Typography align='center' variant={"h5"}>{translations['remove_bios_uefi_passwords'] || 'Remove BIOS/UEFI Passwords'}</Typography>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            color={'primary'}
                                            margin={"dense"}
                                            name={'Normal.BiosPasswords.Enabled'}
                                            checked={values?.Normal?.BiosPasswords?.Enabled || false}
                                            onChange={handleChange}
                                            disabled={enabledField(values)}
                                        />
                                    }
                                    label={`${translations['enabled'] || 'Enabled'}`}
                                />
                            </FormGroup>
                            {values?.Normal?.BiosPasswords?.Passwords?.map((password, index) => (
                                <React.Fragment key={index}>
                                    <Grid container direction="row" justify="center" alignItems="center" spacing={5}>
                                        <Grid item xs={12} sm={9}>
                                            <BiosPasswordForm translations={translations} index={index} formikProps={formikProps} />
                                        </Grid>
                                        <Grid container xs={12} sm={3} direction="row" justify="space-evenly">
                                            <Button
                                                type="button"
                                                variant="contained"
                                                onClick={() => moveBiosPasswordUp(index)}
                                            >
                                                <ArrowUpward />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="contained"
                                                onClick={() => moveBiosPasswordDown(index)}
                                            >
                                                <ArrowDownward />
                                            </Button>
                                            <Button
                                                type="button"
                                                variant="contained"
                                                onClick={() => removeBiosPassword(index)}
                                            >
                                                <Delete />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <Divider className={classes.wifiDivider} variant="middle" />
                                </React.Fragment>
                            ))}
                            <Button
                                className={classes.addButton}
                                variant="contained"
                                color={formSettings.ButtonSubmit.Color}
                                type="button"
                                disabled={!isValid}
                                onClick={addBiosPassword}
                            >
                                {translations['add_bios_uefi_password'] || 'Add BIOS/UEFI password'}
                            </Button>

                            <Divider className={classes.divider} variant="middle" />
                            <br />

                            <Typography align="center" variant={"h5"}>{translations['erasure_mobile'] || 'Erasure Mobile'}</Typography>

                            <Grid container direction="row" justify="center" alignItems="center" className={classes.geolocation}>
                                <Grid item xs={12} sm={11}>
                                    <InputLabel>{translations["different_solution_for_android_and_ios"] || "Different erasure solution for Android and iOS"}</InputLabel>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <Switch name={"DifferentMobileSolutions"} value={values.DifferentMobileSolutions || false} checked={values.DifferentMobileSolutions || false} onChange={handleChangeDifferentMobileSolutions} disabled={enabledField(values)} />
                                </Grid>
                            </Grid>
                            {!values.DifferentMobileSolutions ? <PresetMobileSettings
                                classes={classes}
                                translations={translations}
                                values={values}
                                methods={methods}
                                securazeSolutions={securazeSolutions}
                                verify={verificationMethods}
                                valueText={valueText}
                                setFieldValue={setFieldValue}
                                handleChange={handleChange}
                                skuPatterns={mobileSKUPatterns}
                            /> :
                                <Grid container direction="row" justify="center" alignItems="center" spacing={5}>
                                    <Grid item xs={12} sm={6}>
                                        <InputLabel className={classes.androidIos}>Android</InputLabel>
                                        <PresetMobileSettings
                                            classes={classes}
                                            translations={translations}
                                            values={values}
                                            methods={methods}
                                            securazeSolutions={securazeSolutions}
                                            verify={verificationMethods}
                                            valueText={valueText}
                                            setFieldValue={setFieldValue}
                                            handleChange={handleChange}
                                            osType={'Android'}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <InputLabel className={classes.androidIos}>iOS</InputLabel>
                                        <PresetMobileSettings
                                            classes={classes}
                                            translations={translations}
                                            values={values}
                                            methods={methods}
                                            securazeSolutions={securazeSolutions}
                                            verify={verificationMethods}
                                            valueText={valueText}
                                            setFieldValue={setFieldValue}
                                            handleChange={handleChange}
                                            osType={'iOS'}
                                        />
                                    </Grid>
                                </Grid>
                            }
                            <FormControl className={classes.formControl} margin={"dense"}>
                                <InputLabel shrink>{translations["sku_select"] || "Please select SKU pattern"}</InputLabel>
                                <Select
                                    name={"ErasureMobile.SKUPatternID"}
                                    value={values?.ErasureMobile?.SKUPatternID || ''}
                                    label={translations["sku_pattern"] || 'SKU Pattern'}
                                    margin={"dense"}
                                    onChange={handleChange}
                                    disabled={enabledField(values)}
                                >
                                    {(mobileSKUPatterns ? mobileSKUPatterns : []).map(option => (
                                        <MenuItem key={option.SKUPatternID} value={option.SKUPatternID}>
                                            {option.Name} - {option.Pattern}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            <Grid container direction="row" justify="center" alignItems="center" className={classes.geolocation}>
                                <Grid item xs={12} sm={11}>
                                    <InputLabel>{translations["collect_mobile_ops_geolocation"] || "Collect MobileOps geolocation"}</InputLabel>
                                </Grid>
                                <Grid item xs={12} sm={1}>
                                    <Switch name={"CollectMobileOpsgeolocation"} value={values.CollectMobileOpsgeolocation || false} checked={values.CollectMobileOpsgeolocation || false} onChange={handleChange} disabled={enabledField(values)} />
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <FormGroup>
                                        <FormControlLabel
                                            control={
                                                <Checkbox
                                                    color={'primary'}
                                                    margin={"dense"}
                                                    name={"AutoshutdownMobile"}
                                                    checked={(values.AutoshutdownMobile) || false}
                                                    disabled={enabledField(values)}
                                                    onChange={handleChange}
                                                />
                                            }
                                            label={`${translations['autoshutdown_mobile'] || 'Auto shut down at specific time (Mobile)'}`}
                                        />
                                        {
                                            values["AutoshutdownMobile"] && 
                                            <TimePicker
                                                clearable
                                                name={'AutoshutdownMobileTime'}
                                                onChange={(time) => setFieldValue('AutoshutdownMobileTime', time.format('HH:mm'))}
                                                value={moment(values['AutoshutdownMobileTime'] || '22:00', 'HH:mm')}
                                            />
                                        }
                                    </FormGroup>
                                </Grid>
                            </Grid>
                            <Divider className={classes.divider} variant="middle" />
                            <br />

                            <br />
                            <Typography align='center' variant={"h5"}>{translations['wifis'] || 'Wifis'}</Typography>
                            {values?.Wifis?.map((wifi, index) => (
                                <React.Fragment key={index}>
                                    <Grid container direction="row" justify="center" alignItems="center" spacing={5}>
                                        <Grid item xs={12} sm={10}>
                                            <WifiForm translations={translations} index={index} formikProps={formikProps} />
                                        </Grid>
                                        <Grid item xs={12} sm={2}>
                                            <Button
                                                type="button"
                                                variant="contained"
                                                onClick={() => removeWifi(index)}
                                            >
                                                <Delete />
                                            </Button>
                                        </Grid>
                                    </Grid>
                                    <Divider className={classes.wifiDivider} variant="middle" />
                                </React.Fragment>
                            ))}
                            <Button
                                className={classes.addButton}
                                variant="contained"
                                color={formSettings.ButtonSubmit.Color}
                                type="button"
                                disabled={!isValid}
                                onClick={addWifi}
                            >
                                {translations['add_wifi'] || 'Add wifi'}
                            </Button>

                            {/*Customization*/}
                            {/*<Divider className={classes.divider} variant="middle" />*/}
                            {/*<br />*/}

                            {/*<Grid container direction="row" justify="center" alignItems="center" className={classes.geolocation}>*/}
                            {/*    <Grid item xs={12} sm={11}>*/}
                            {/*        <InputLabel>{translations["enable_customization"] || "Enable customization"}</InputLabel>*/}
                            {/*    </Grid>*/}
                            {/*    <Grid item xs={12} sm={1}>*/}
                            {/*        <Switch name={"EnableCustomization"} value={values.EnableCustomization || false} checked={values.EnableCustomization || false} onChange={handleChange} disabled={enabledField(values)} />*/}
                            {/*    </Grid>*/}
                            {/*</Grid>*/}

                            {/*{values.EnableCustomization && <CustomizationPresets values={values} setFieldValue={setFieldValue} translations={translations} handleChange={handleChange} errors={errors} touched={touched} />}*/}

                            <div>
                                {formSettings.ButtonSubmit ? (
                                    <Button
                                        className={classes.submit}
                                        variant="contained"
                                        color={formSettings.ButtonSubmit.Color}
                                        type="submit"
                                        disabled={!isValid || isSubmitting || loading}
                                    >
                                        {isSubmitting ? <CircularProgress size={24} /> : formSettings.ButtonSubmit.Title}
                                    </Button>
                                ) : null}
                            </div>
                        </form>
                    );
                }}
            </Formik>
        </div >
    );
}

export default withStyles(styles, { withTheme: true })(PresetForm);
